import * as React from 'react';
import { cloneDeep } from 'lodash';
import { TextInput, WhiteLabelMobileSpeechBox } from '@mitchell/react.component.ux';
import { EMPTY_TEXT, TranslationService } from "../../common";
import { COMMENT_TEXT_MAX_CHARACTER, ADD_COMMENTS_PLACEHOLDER } from "../../brandedNotification";
import { DAMAGE_PANEL, PhotoCaptureConstants, FOLDER_DISCLAIMER_WARNING } from '../constants';
import { PhotoCapture, PhotoCaptureAttributeType } from '../enums';
import { isInvalidFile, getPhotoCaptureProgressiveCurrentAttachmentData, getProgressiveAttachmentIdentifier, getPRGPanelParentImages, getNavigationMultipleButtonProps, getFolderModelInfo } from '../utilities';
import { ConnectedPhotoCaptureDamagePanelContainer } from '../containers';
import { StyledActionControlsSection } from './SinglePhotoUploadComponent.style';
import { StyledMultiCommentContainer } from './PhotoCaptureMultiCommentComponent.style';
import { SpeechBoxSectionContainer, StyleDamagePanelGridContainer } from './PhotoCaptureDamagePanelComponent.style';
import { IntelligentPageGuidanceInfo, IntelligentPBEContainerComponent } from './intelligentComponents';
import { CarrierSectionContainer } from './intelligentComponents/IntelligentScriptQuestion.style';
export const ProgressiveWideShotComponent = ({ appTheme, questionText, companyCode, questionnaireQuestionId, questionSubType, photoCaptureAttachmentInfo, warningMessage, parentQuestionId, photoCaptureFileUploadFailedAction, photoCaptureIsFileUploadStartedAction, setSelectedImage, setImageData, setIsImageUpdated, imageData = [], moveNext, movePrevious, shouldShowCommentBox, isMic, folderOptionList, language, contextId, photoCaptureGetFoldersRequestAction, missingDamages }) => {
    var _a;
    const translateFn = TranslationService.translate;
    const imageDocId = (_a = imageData[0]) === null || _a === void 0 ? void 0 : _a.thumbnailDocUId;
    const [comment, setComment] = React.useState(EMPTY_TEXT);
    const [updatedWarningMessage, setUpdatedWarningMessage] = React.useState(EMPTY_TEXT);
    const onFileUploadedSuccess = (files, panelName, isParentImage, additionalImageTag, fileMetadataDesc, damageId) => {
        if (!isInvalidFile(files)) {
            const attachmentIdentifier = getProgressiveAttachmentIdentifier(photoCaptureAttachmentInfo, imageData, isParentImage, isParentImage ? panelName : additionalImageTag);
            let parentIdentifier = 0;
            if (isParentImage) {
                parentIdentifier = attachmentIdentifier;
            }
            else {
                const parentImage = imageData.find(image => image.damageName === panelName && image.isParentImage);
                if (parentImage && parentImage.parentIdentifier) {
                    parentIdentifier = parentImage.parentIdentifier;
                }
            }
            if (files[0].docUID && files[0].thumbnail && files[0].thumbnail.docUID) {
                const commentText = (questionSubType === PhotoCaptureAttributeType.WIDE_ENHANCED && isParentImage && comment) || EMPTY_TEXT;
                const currentImageData = getPhotoCaptureProgressiveCurrentAttachmentData(files[0], fileMetadataDesc, attachmentIdentifier, parentQuestionId, questionnaireQuestionId, commentText, isParentImage ? attachmentIdentifier : parentIdentifier, isParentImage, panelName, additionalImageTag, damageId, getFolderModelInfo(damageId, folderOptionList));
                if (isParentImage) {
                    const indexToUpdate = Number(additionalImageTag.split('_')[1]);
                    setImageData &&
                        setImageData([...imageData.slice(0, indexToUpdate), currentImageData, ...imageData.slice(indexToUpdate)]);
                }
                else {
                    const filteredImageData = imageData.filter((image) => image.attachmentIdentifier !== attachmentIdentifier);
                    setImageData && setImageData([...filteredImageData, currentImageData]);
                }
                setIsImageUpdated && setIsImageUpdated(true);
            }
        }
        else {
            photoCaptureIsFileUploadStartedAction(false);
            photoCaptureFileUploadFailedAction();
        }
    };
    const onHandleCommentText = (value) => {
        const imageDataContent = cloneDeep(imageData);
        const findParentImageIndex = imageDataContent.findIndex((damage) => damage.isParentImage);
        const prevImageComment = imageDataContent[findParentImageIndex].commentText || EMPTY_TEXT;
        setComment(value);
        imageDataContent[findParentImageIndex] = Object.assign(Object.assign({}, imageDataContent[findParentImageIndex]), { commentText: value });
        setImageData && setImageData(imageDataContent);
        setIsImageUpdated && setIsImageUpdated(prevImageComment !== value);
    };
    const getDamagePanelProps = () => {
        const commonProps = {
            twistyLabel: DAMAGE_PANEL.TWISTY_LABEL,
            twistyIndex: 0,
            onFileUploadedSuccess,
            buttonBackgroundColor: appTheme.primaryButtonBackgroundColor,
            imageData,
            setImageData,
            setIsImageUpdated,
            setSelectedImage,
            imageMaxWidth: undefined,
            companyCode,
            disableLibrary: undefined,
            fileSizeLimit: 0,
            folderOptionList,
            language
        };
        if (questionSubType === PhotoCaptureAttributeType.CLOSEUP_ENHANCED) {
            return Object.assign(Object.assign({}, commonProps), { isDeleteButtonVisible: true });
        }
        return commonProps;
    };
    const multipleButtonProps = Object.assign(Object.assign(Object.assign({}, getNavigationMultipleButtonProps({
        appTheme: Object.assign(Object.assign({}, appTheme), { selectedButtonBackgroundColor: appTheme.buttonBackgroundColor }),
        onLeftClick: () => movePrevious && movePrevious(),
        onRightClick: () => moveNext && moveNext()
    })), { rightButtonLabel: PhotoCaptureConstants.NEXT, shouldNextButtonDisabled: !imageData[0] }), (!isMic && {
        leftButtonLabel: PhotoCaptureConstants.PREVIOUS,
        leftButtonIcon: ''
    }));
    React.useEffect(() => {
        (!folderOptionList || folderOptionList.length === 0) && photoCaptureGetFoldersRequestAction(companyCode, contextId);
    }, []);
    React.useEffect(() => {
        if (imageData && imageData.length) {
            setComment(getPRGPanelParentImages(imageData)[0].commentText || EMPTY_TEXT);
        }
        else {
            setComment(EMPTY_TEXT);
        }
    }, [imageData]);
    React.useEffect(() => {
        setUpdatedWarningMessage(warningMessage);
    }, [warningMessage]);
    // Display the folder disclaimer and setIsImageUpdated to true based on missingDamages
    React.useEffect(() => {
        if (missingDamages && missingDamages.length) {
            setIsImageUpdated && setIsImageUpdated(true);
            setUpdatedWarningMessage(TranslationService.translate(FOLDER_DISCLAIMER_WARNING, {
                damageName: missingDamages.join(', ')
            }));
        }
    }, [missingDamages]);
    return (React.createElement(IntelligentPBEContainerComponent, Object.assign({ warningMessage: updatedWarningMessage, islanguageHide: true, appTheme: appTheme, btnLabel: TranslationService.translate(PhotoCaptureConstants.NEXT), isFooter: isMic, hasMultipleButton: !isMic, isBtnDisabled: !imageData[0] }, (isMic && { movePrevious: () => movePrevious && movePrevious() }), { moveNext: () => moveNext && moveNext(), multipleButtonProps: multipleButtonProps, shouldAllowMitchellIntelligent: isMic }),
        React.createElement(StyledActionControlsSection, null,
            !isMic ? (React.createElement(SpeechBoxSectionContainer, null,
                React.createElement(WhiteLabelMobileSpeechBox, { speechBoxRichText: questionText, speechBoxBackgroundColor: appTheme.speechBoxBackgroundColor }))) : (React.createElement(CarrierSectionContainer, null,
                React.createElement(IntelligentPageGuidanceInfo, { instructions: questionText }))),
            React.createElement(StyleDamagePanelGridContainer, null,
                React.createElement(ConnectedPhotoCaptureDamagePanelContainer, Object.assign({}, getDamagePanelProps()))),
            !isMic && shouldShowCommentBox && questionSubType === PhotoCaptureAttributeType.WIDE_ENHANCED && (React.createElement(StyledMultiCommentContainer, null,
                React.createElement(TextInput, { textLabel: EMPTY_TEXT, showRemainingCharAt: COMMENT_TEXT_MAX_CHARACTER, id: `${PhotoCapture.GUIDED_PHOTO_CAPTURE}_PRGWideShot`, isClearBtnRequired: false, maxInputLength: COMMENT_TEXT_MAX_CHARACTER, onChange: onHandleCommentText, value: comment, placeHolder: translateFn(ADD_COMMENTS_PLACEHOLDER), translateFn: translateFn, isDisabled: !imageDocId }))))));
};
//# sourceMappingURL=ProgressiveWideShotComponent.js.map